import { motion } from 'framer-motion';
import { Zap, LayoutDashboard, Box, ArrowUpRight } from 'lucide-react';
// Box kept for 3D-printing teaser below
import { Link } from 'react-router-dom';

const services = [
  {
    icon: Zap,
    tag: 'Automationer',
    title: 'AI-Automationer',
    description: 'Vi automatiserar repetitiva processer med AI-workflows som sparar tid och pengar. Från lead-hantering till fakturaflöden — din personal fokuserar på det som verkligen spelar roll.',
    href: '/ai-consulting',
    gradient: 'from-amber-400 to-orange-500',
    glow: 'rgba(251,146,60,0.3)',
    border: 'hover:border-amber-400/40',
    tagColor: 'text-amber-600 bg-amber-50',
  },
  {
    icon: LayoutDashboard,
    tag: 'SaaS',
    title: 'SaaS-applikationer',
    description: 'Egenutvecklade SaaS-produkter som löser riktiga problem för riktiga användare. Vi designar, bygger och lanserar — med AI som kärna. Klagobot och Swishkassa är två av våra egna.',
    href: '/ai-consulting',
    gradient: 'from-indigo-500 to-violet-600',
    glow: 'rgba(99,102,241,0.35)',
    border: 'hover:border-indigo-400/50',
    tagColor: 'text-indigo-600 bg-indigo-50',
  },
];

const CorporateServices = () => {
  return (
    <section className="py-28 bg-white">
      <div className="container mx-auto px-6">

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-60px' }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="text-center mb-20"
        >
          <span className="inline-block text-xs font-semibold tracking-[0.18em] uppercase text-brand-indigo mb-4">
            Vad vi gör
          </span>
          <h2 className="text-4xl sm:text-[46px] font-extrabold text-text-primary tracking-[-0.02em] leading-[1.1] mb-5">
            Vad vi<br />
            <span className="bg-gradient-to-r from-indigo-600 via-violet-600 to-purple-600 bg-clip-text text-transparent">
              bygger
            </span>
          </h2>
          <p className="text-lg text-text-secondary max-w-lg mx-auto leading-relaxed">
            Konkreta verktyg och automationer som faktiskt används — inte PowerPoints om framtiden.
          </p>
        </motion.div>

        {/* Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto">
          {services.map((s, i) => (
            <motion.div
              key={s.tag}
              initial={{ opacity: 0, y: 32 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-40px' }}
              transition={{ delay: i * 0.12, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              whileHover={{ y: -6 }}
              className={`group relative bg-white rounded-2xl border border-border-light p-8 transition-all duration-300 ${s.border}`}
              style={{ transition: 'box-shadow 0.3s ease, transform 0.3s ease, border-color 0.3s ease' }}
              onMouseEnter={e => {
                (e.currentTarget as HTMLElement).style.boxShadow = `0 20px 60px ${s.glow}, 0 4px 20px rgba(0,0,0,0.08)`;
              }}
              onMouseLeave={e => {
                (e.currentTarget as HTMLElement).style.boxShadow = '';
              }}
            >
              {/* Icon */}
              <div className={`inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-gradient-to-br ${s.gradient} mb-6 shadow-lg`}
                style={{ boxShadow: `0 8px 20px ${s.glow}` }}>
                <s.icon className="w-5 h-5 text-white" />
              </div>

              {/* Tag */}
              <span className={`inline-block text-[10px] font-bold tracking-widest uppercase px-2.5 py-1 rounded-full mb-3 ${s.tagColor}`}>
                {s.tag}
              </span>

              {/* Title */}
              <h3 className="text-[22px] font-bold text-text-primary mb-3 tracking-tight leading-snug">
                {s.title}
              </h3>

              {/* Description */}
              <p className="text-[15px] text-text-secondary leading-relaxed mb-8">
                {s.description}
              </p>

              {/* Link */}
              <Link
                to={s.href}
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-text-primary group-hover:text-brand-indigo transition-colors duration-200"
              >
                Läs mer
                <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-200" />
              </Link>

              {/* Gradient top line on hover */}
              <div className={`absolute top-0 left-6 right-6 h-[2px] rounded-b-full bg-gradient-to-r ${s.gradient} opacity-0 group-hover:opacity-100 transition-opacity duration-300`} />
            </motion.div>
          ))}
        </div>

        {/* 3D-printing secondary mention */}
        <div className="mt-8 max-w-3xl mx-auto">
          <Link
            to="/3d-printing"
            className="group flex items-center justify-between bg-surface-subtle rounded-2xl border border-border-light px-6 py-4 hover:border-indigo-200 transition-colors duration-200"
          >
            <div className="flex items-center gap-4">
              <div className="inline-flex w-9 h-9 rounded-xl bg-gradient-to-br from-emerald-400 to-teal-500 items-center justify-center shadow-sm">
                <Box className="w-4 h-4 text-white" />
              </div>
              <div>
                <p className="text-sm font-semibold text-text-primary">Tillverkar ni fysiska produkter?</p>
                <p className="text-xs text-text-secondary">Vi erbjuder även 3D-printing — prototyper och reservdelar på 24–72 timmar.</p>
              </div>
            </div>
            <ArrowUpRight className="w-4 h-4 text-text-muted group-hover:text-brand-indigo transition-colors" />
          </Link>
        </div>

      </div>
    </section>
  );
};

export default CorporateServices;
